<template>
    <div>
        <Modal
            :showHideButton="false"
            :isClickOutsideEnabled="false"
            v-show="!isDecided || showSettings"
            ref="modal_cookie_consent"
            width="w-5/6 md:w-2/3 lg:w-1/2"
        >
            <template v-slot:title>
                {{
                    showSettings
                        ? $t("cookie_consent.settings_title")
                        : $t("cookie_consent.title")
                }}
            </template>
            <template v-slot:body>
                <div v-if="!showSettings">
                    <i18n path="cookie_consent.body" tag="p" class="mb-4">
                        <a
                            href="https://www.roatel.com/datenschutz-booking"
                            class="text-r-green underline"
                            target="_blank"
                            >{{ $t("cookie_consent.privacy_label") }}</a
                        >
                    </i18n>
                </div>
                <div v-else>
                    <!-- Cookie Settings -->
                    <div class="space-y-4">
                        <!-- Necessary Cookies -->
                        <div class="border-b pb-4">
                            <div class="flex items-center justify-between mb-2">
                                <h4 class="font-semibold">
                                    {{ $t("cookie_consent.necessary") }}
                                </h4>
                                <input
                                    type="checkbox"
                                    checked
                                    disabled
                                    class="opacity-50 text-r-green"
                                />
                            </div>
                            <p class="text-sm text-gray-600">
                                {{ $t("cookie_consent.necessary_desc") }}
                            </p>
                        </div>

                        <!-- Analytics Cookies -->
                        <div class="border-b pb-4">
                            <div class="flex items-center justify-between mb-2">
                                <h4 class="font-semibold">
                                    {{ $t("cookie_consent.analytics") }}
                                </h4>
                                <input
                                    type="checkbox"
                                    v-model="preferences.analytics"
                                    class="form-checkbox text-r-green focus:ring-r-green"
                                />
                            </div>
                            <p class="text-sm text-gray-600">
                                {{ $t("cookie_consent.analytics_desc") }}
                            </p>
                        </div>

                        <!-- Marketing Cookies -->
                        <div class="border-b pb-4">
                            <div class="flex items-center justify-between mb-2">
                                <h4 class="font-semibold">
                                    {{ $t("cookie_consent.marketing") }}
                                </h4>
                                <input
                                    type="checkbox"
                                    v-model="preferences.marketing"
                                    class="form-checkbox text-r-green focus:ring-r-green"
                                />
                            </div>
                            <p class="text-sm text-gray-600">
                                {{ $t("cookie_consent.marketing_desc") }}
                            </p>
                        </div>
                    </div>

                    <p class="text-sm text-gray-500 mt-4 mb-4">
                        <i18n path="cookie_consent.more_info" tag="span">
                            <a
                                href="https://www.roatel.com/datenschutz-booking"
                                class="text-r-green underline"
                                target="_blank"
                                >{{ $t("cookie_consent.privacy_label") }}</a
                            >
                        </i18n>
                    </p>
                </div>
            </template>
            <template v-slot:footer>
                <div
                    v-if="showSettings"
                    class="flex flex-col md:flex-row items-center justify-between"
                >
                    <div>
                        <button
                            class="text-sm text-gray-500 hover:text-gray-700"
                            @click="showSettings = false"
                        >
                            {{ $t("cookie_consent.back_label") }}
                        </button>
                    </div>
                    <div class="flex flex-col md:flex-row gap-2">
                        <app-button
                            color="gray"
                            :outline="false"
                            @click="acceptSelected"
                        >
                            {{ $t("cookie_consent.save_preferences") }}
                        </app-button>
                        <app-button
                            color="green"
                            size="lg"
                            :outline="false"
                            @click="acceptAll"
                        >
                            {{ $t("cookie_consent.accept_all") }}
                        </app-button>
                    </div>
                </div>
                <div
                    v-if="!showSettings"
                    class="flex md:flex-row items-center justify-between flex-col gap-5 md:gap-0"
                >
                    <div class="flex flex-col md:flex-row gap-5 md:gap-10">
                        <button
                            class="text-sm text-gray-500 hover:text-gray-700"
                            @click="decide(false)"
                        >
                            {{ $t("cookie_consent.reject_label") }}
                        </button>
                        <button
                            class="text-sm text-gray-500 hover:text-gray-700"
                            @click="showSettings = true"
                        >
                            {{ $t("cookie_consent.customize_label") }}
                        </button>
                    </div>
                    <div>
                        <app-button
                            color="green"
                            size="lg"
                            :outline="false"
                            @click="acceptAll"
                        >
                            {{ $t("cookie_consent.accept_all") }}
                        </app-button>
                    </div>
                </div>
            </template>
        </Modal>

        <!-- Consent Management Button (Fixed Position) -->
        <!-- <div v-if="isDecided" class="fixed bottom-4 left-4 z-50"> -->
        <!--     <button -->
        <!--         @click="showSettings = true" -->
        <!--         class="bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-2 rounded-lg text-xs shadow-lg border" -->
        <!--         :title="$t('cookie_consent.manage_cookies')" -->
        <!--     > -->
        <!--         🍪 {{ $t("cookie_consent.manage_cookies") }} -->
        <!--     </button> -->
        <!-- </div> -->
    </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import Modal from "@guest/common/Modal";
import AppButton from "@guest/common/Button";
import {
    updateGtmConsent,
    isGtmAvailable,
    loadGtmIfConsented,
    initializeGtm,
} from "@/guest/utils/gtm";

export default {
    components: {
        Modal,
        AppButton,
    },
    data() {
        return {
            showSettings: false,
            preferences: {
                necessary: true, // Always true, cannot be disabled
                analytics: false,
                marketing: false,
            },
        };
    },
    methods: {
        acceptAll() {
            this.preferences.analytics = true;
            this.preferences.marketing = true;
            this.decide(true);
        },
        acceptSelected() {
            this.decide(
                this.preferences.analytics || this.preferences.marketing
            );
        },
        decide(payload) {
            // For reject all case, ensure all non-necessary preferences are false
            if (!payload) {
                this.preferences.analytics = false;
                this.preferences.marketing = false;
            }

            const consentData = {
                isDecided: true,
                isAllowed: !!payload,
                preferences: { ...this.preferences },
                timestamp: new Date().getTime(),
                expires: new Date().getTime() + 365 * 24 * 60 * 60 * 1000, // 1 year
                version: "1.0", // Track consent version for GDPR compliance
                userAgent: navigator.userAgent, // Track browser for audit
                url: window.location.href, // Track page where consent was given
            };

            this.setCookieConsent({
                ...consentData,
            });

            this.showSettings = false;
            this.updateCookieState();
        },
        ...mapActions({
            setCookieConsent: "setCookieConsent",
        }),
        updateCookieState() {
            const preferences =
                this.getCookieConsent?.preferences || this.preferences;
            console.log(
                "gtm updateCookieState",
                preferences,
                this.isDecided,
                this.isAllowed,
                preferences.analytics,
                preferences.marketing
            );

            if (
                this.isDecided &&
                (this.isAllowed ||
                    preferences.analytics ||
                    preferences.marketing)
            ) {
                console.log("cookie: consent granted, updating GTM");

                // Load GTM only when consent is granted (GDPR compliant)
                loadGtmIfConsented()
                    .then(() => {
                        // Update GTM consent based on specific preferences after loading
                        setTimeout(() => {
                            updateGtmConsent(
                                {
                                    ad_user_data: preferences.marketing
                                        ? "granted"
                                        : "denied",
                                    ad_personalization: preferences.marketing
                                        ? "granted"
                                        : "denied",
                                    ad_storage: preferences.marketing
                                        ? "granted"
                                        : "denied",
                                    analytics_storage: preferences.analytics
                                        ? "granted"
                                        : "denied",
                                },
                                true
                            ); // Enable audit logging
                        }, 500);
                    })
                    .catch((error) => {
                        console.error(
                            "Failed to load GTM after consent:",
                            error
                        );
                    });
            } else {
                // Ensure consent remains denied when all cookies are rejected
                console.log("gtm cookie: tracking disabled");
                // Don't load GTM script when consent is denied
                // But if GTM infrastructure exists, ensure consent is properly denied
                if (isGtmAvailable() || typeof window.gtag === "function") {
                    updateGtmConsent(
                        {
                            ad_user_data: "denied",
                            ad_personalization: "denied",
                            ad_storage: "denied",
                            analytics_storage: "denied",
                        },
                        false
                    );
                }
            }
        },
        checkConsentExpiry() {
            if (
                this.getCookieConsent?.expires &&
                new Date().getTime() > this.getCookieConsent.expires
            ) {
                // Consent has expired, reset it
                this.setCookieConsent({
                    isDecided: false,
                    isAllowed: false,
                    preferences: {
                        necessary: true,
                        analytics: false,
                        marketing: false,
                    },
                });
            }
        },
        loadPreferences() {
            if (this.getCookieConsent?.preferences) {
                this.preferences = { ...this.getCookieConsent.preferences };
            }
        },
    },
    watch: {
        getCookieConsent() {
            console.log("watcher cookie consent", this.getCookieConsent);
            if (!this.isDecided) {
                this.$refs.modal_cookie_consent.show();
            } else {
                this.loadPreferences();
            }
        },
        showSettings(newVal) {
            if (newVal) {
                this.loadPreferences();
                this.$refs.modal_cookie_consent.show();
            } else {
                this.loadPreferences();
            }
        },
    },
    computed: {
        ...mapGetters({
            getCookieConsent: "getCookieConsent",
        }),
        isDecided() {
            if (this.getCookieConsent) {
                return this.getCookieConsent.isDecided;
            }
            return false;
        },
        isAllowed() {
            if (this.getCookieConsent) {
                return this.getCookieConsent.isAllowed;
            }
            return false;
        },
    },
    mounted() {
        // Initialize GTM infrastructure (GDPR compliant - no tracking until consent)
        initializeGtm();

        this.checkConsentExpiry();
        console.log("cookie component state", this.getCookieConsent);

        // Show consent banner on first visit or if consent expired/not decided
        if (!this.isDecided || !this.getCookieConsent) {
            this.$refs.modal_cookie_consent.show();
        }

        if (this.isDecided) {
            this.updateCookieState();
        }
    },
    props: {},
};
</script>

<style lang="scss"></style>
